import { memo } from 'react';

import { CreditCard, Download, Gear, Uploads, User } from '@air/next-icons';
import { Button } from '@air/primitive-button';
import { useAirModal } from '@air/provider-modal';
import { type Board } from '@air/types';

import { EditBoardModal } from '~/components/EditPrivateBoardModal/EditBoardModal/EditBoardModal';
import { ExportImagesModal } from '~/components/ExportImagesModal/ExportImagesModal';
import { AddAssetsToBoardsModal } from '~/components/Modals/AddAssetsToBoardsModal/AddAssetsToBoardsModal';
import { BulkDownloadAssetsModal } from '~/components/Modals/BulkDownloadAssetsModal';
import { CreateBoardModal } from '~/components/Modals/CreateBoardModal';
import { DeleteBoardModal } from '~/components/Modals/DeleteBoards/DeleteBoardsModal';
import { DuplicateBoardModal } from '~/components/Modals/DuplicateBoard';
import { InviteMembersModal } from '~/components/Modals/InviteMembers/InviteMembersModal';
import { LoginModal } from '~/components/Modals/LoginModal/LoginModal';
import { MoveItemsModal } from '~/components/Modals/MoveItemsModal/MoveItemsModal';
import { PlansModal } from '~/components/Modals/PlansModal/PlansModal';
import { SignupModal } from '~/components/Modals/SignupModal/SignUpModal';
import { WorkspaceIdModal } from '~/components/Modals/WorkspaceIdModal';

/**
 * Modals tab providing quick access to various application modals.
 */
export const ModalsTab = memo(() => {
  // Modal launchers
  const [showPlansModal] = useAirModal(PlansModal);
  const [showInviteMembersModal] = useAirModal(InviteMembersModal);
  const [showWorkspaceIdModal] = useAirModal(WorkspaceIdModal);
  const [showCreateBoardModal] = useAirModal(CreateBoardModal);
  const [showDuplicateBoardModal] = useAirModal(DuplicateBoardModal);
  const [showEditBoardModal] = useAirModal(EditBoardModal);
  const [showDeleteBoardsModal] = useAirModal(DeleteBoardModal);
  const [showMoveItemsModal] = useAirModal(MoveItemsModal);
  const [showAddAssetsToBoardsModal] = useAirModal(AddAssetsToBoardsModal);
  const [showBulkDownloadAssetsModal] = useAirModal(BulkDownloadAssetsModal);
  const [showExportImagesModal] = useAirModal(ExportImagesModal);
  const [showLoginModal] = useAirModal(LoginModal);
  const [showSignupModal] = useAirModal(SignupModal);

  return (
    <div className="h-full overflow-y-auto p-3">
      <div className="space-y-6">
        {/* Core Modals */}
        <section>
          <h3 className="mb-3 font-medium text-12 text-grey-9">Core</h3>
          <div className="grid grid-cols-2 gap-4">
            <Button
              appearance="outline"
              color="grey"
              className="h-auto px-4 py-3 text-grey-12"
              onClick={() => showPlansModal()}
              prefix={<CreditCard className="size-4" />}
            >
              Plans
            </Button>
            <Button
              appearance="outline"
              color="grey"
              className="h-auto px-4 py-3 text-grey-12"
              onClick={() => showInviteMembersModal({ location: 'header' })}
              prefix={<User className="size-4" />}
            >
              Invite Members
            </Button>
            <Button
              appearance="outline"
              color="grey"
              className="h-auto px-4 py-3 text-grey-12"
              onClick={() => showWorkspaceIdModal()}
              prefix={<Gear className="size-4" />}
            >
              Workspace ID
            </Button>
            <Button
              appearance="outline"
              color="grey"
              className="h-auto px-4 py-3 text-grey-12"
              onClick={() => window.open('/import', '_blank')}
              prefix={<Uploads className="size-4" />}
            >
              Import
            </Button>
          </div>
        </section>

        {/* Board Management */}
        <section>
          <h3 className="mb-3 font-medium text-12 text-grey-9">Board Management</h3>
          <div className="grid grid-cols-2 gap-4">
            <Button
              appearance="outline"
              color="grey"
              className="h-auto px-4 py-3 text-grey-12"
              onClick={() => showCreateBoardModal({ trackLocation: 'dev-modal' })}
              prefix={<Gear className="size-4" />}
            >
              Create Board
            </Button>
            <Button
              appearance="outline"
              color="grey"
              className="h-auto px-4 py-3 text-grey-12"
              onClick={() => showDuplicateBoardModal({ boards: [], trackLocation: 'dev-modal' })}
              prefix={<Gear className="size-4" />}
            >
              Duplicate Board
            </Button>
            <Button
              appearance="outline"
              color="grey"
              className="h-auto px-4 py-3 text-grey-12"
              onClick={() =>
                showEditBoardModal({
                  trackLocation: 'dev-modal',
                  board: { id: '', title: '', description: '' } as Board,
                })
              }
              prefix={<Gear className="size-4" />}
            >
              Edit Board
            </Button>
            <Button
              appearance="outline"
              color="grey"
              className="h-auto px-4 py-3 text-grey-12"
              onClick={() =>
                showDeleteBoardsModal({
                  boardIds: [],
                  trackLocation: 'dev-modal',
                })
              }
              prefix={<Gear className="size-4" />}
            >
              Delete Boards
            </Button>
            <Button
              appearance="outline"
              color="grey"
              className="h-auto px-4 py-3 text-grey-12"
              onClick={() => showMoveItemsModal()}
              prefix={<Gear className="size-4" />}
            >
              Move Items
            </Button>
          </div>
        </section>

        {/* Asset Management */}
        <section>
          <h3 className="mb-3 font-medium text-12 text-grey-9">Asset Management</h3>
          <div className="grid grid-cols-2 gap-4">
            <Button
              appearance="outline"
              color="grey"
              className="h-auto px-4 py-3 text-grey-12"
              onClick={() => showAddAssetsToBoardsModal({ trackLocation: 'dev-modal', clipIds: [] })}
              prefix={<Uploads className="size-4" />}
            >
              Add Assets to Boards
            </Button>
            <Button
              appearance="outline"
              color="grey"
              className="h-auto px-4 py-3 text-grey-12"
              onClick={() =>
                showBulkDownloadAssetsModal({
                  clipIds: [],
                  onDownloadAsZip: () => {},
                  onDownloadIndividually: () => {},
                })
              }
              prefix={<Download className="size-4" />}
            >
              Bulk Download
            </Button>
            <Button
              appearance="outline"
              color="grey"
              className="h-auto px-4 py-3 text-grey-12"
              onClick={() => showExportImagesModal({ width: 800, height: 600, imageSrc: '', fileName: 'export' })}
              prefix={<Download className="size-4" />}
            >
              Export Images
            </Button>
          </div>
        </section>

        {/* Authentication */}
        <section>
          <h3 className="mb-3 font-medium text-12 text-grey-9">Authentication</h3>
          <div className="grid grid-cols-2 gap-2">
            <Button
              appearance="outline"
              color="grey"
              className="h-auto px-4 py-3 text-grey-12"
              onClick={() => showLoginModal()}
              prefix={<User className="size-4" />}
            >
              Login
            </Button>
            <Button
              appearance="outline"
              color="grey"
              className="h-auto px-4 py-3 text-grey-12"
              onClick={() => showSignupModal()}
              prefix={<User className="size-4" />}
            >
              Signup
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
});

ModalsTab.displayName = 'ModalsTab';
